import React from 'react';
import PropTypes from 'prop-types';
import { CCard, CCardBody, CCardImage, CCardTitle, CButton, CCardFooter } from '@coreui/react';
import { getAuth } from 'firebase/auth';
import defaultArtwork from '../assets/watermark.png';
import ListingHistoryModal from './ListingHistoryModal';
import { toast } from 'react-toastify';
import Logger from '../utils/Logger';
import StripeBuyButton from './StripeBuyButton';
import config from '../utils/EnvironmentSwitcher';
import { downloadOwnedNftAsset } from '../utils/downloadAsset';

const ArtworkCard = ({
    title,
    description,
    imageUrl,
    itemType,
    itemSubject,
    itemMedia,
    price,
    isListed,
    onList,
    onCancelListing,
    onUpdatePrice,
    onView,
    listingId,
    nftId,
    isMarketplace = false,
    handleClick,
    onListForSale,
    showListForSale = false,
    status,
    itemAssetUrl,
    itemAssetName,
    itemAssetType,
    assetType,
    userId,
    currentUserId,
    authUser
}) => {
    const [showHistoryModal, setShowHistoryModal] = React.useState(false);
    const [isDownloading, setIsDownloading] = React.useState(false);

    const handleListForSaleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        Logger.debug('List for sale button clicked');
        if (onListForSale) {
            onListForSale();
        }
    };

    const handleDownload = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Ownership gated in UI; API also enforces owner === Firebase uid.
        if (userId !== currentUserId) {
            toast.error('Only the owner can download this asset');
            return;
        }
        if (!nftId && !itemAssetUrl) {
            toast.error('Invalid or missing asset');
            return;
        }

        setIsDownloading(true);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) {
                toast.error('Please sign in to download');
                return;
            }
            const idToken = await user.getIdToken();
            await downloadOwnedNftAsset({
                apiUrl: config.apiUrl,
                idToken,
                nftId,
                ipfsUrl: itemAssetUrl,
                assetName: itemAssetName,
                assetType: itemAssetType || assetType,
            });
        } catch (error) {
            console.error('Error downloading asset:', error);
            toast.error(error.message || 'Failed to download asset');
        } finally {
            setIsDownloading(false);
        }
    };

    const renderAssetButton = () => {
        if ((itemAssetUrl || nftId) && userId === currentUserId) {
            return (
                <CButton
                    color="success"
                    size="sm"
                    onClick={handleDownload}
                    className="me-2"
                    disabled={isDownloading}
                >
                    <i className="fas fa-download me-1"></i>
                    {isDownloading ? 'Downloading…' : 'Download Asset'}
                </CButton>
            );
        }
        return null;
    };

    const renderActionButton = () => {
        switch (status) {
            case 'LISTED':
                return (
                    <div>
                        <div className="text-success mb-2">Listed for {price} MIE</div>
                        <div className="d-flex gap-2">
                            {renderAssetButton()}
                            <CButton
                                color="danger"
                                size="sm"
                                onClick={() => onCancelListing(listingId)}
                            >
                                Cancel Listing
                            </CButton>
                            <CButton
                                color="info"
                                size="sm"
                                onClick={() => onView && onView()}
                            >
                                View Details
                            </CButton>
                        </div>
                    </div>
                );
            case 'CREATED':
                return (
                    <div className="d-flex gap-2">
                        {renderAssetButton()}
                        <CButton
                            color="primary"
                            size="sm"
                            onClick={onListForSale}
                        >
                            List for Sale
                        </CButton>
                        <CButton
                            color="info"
                            size="sm"
                            onClick={() => onView && onView()}
                        >
                            View Details
                        </CButton>
                    </div>
                );
            case 'SOLD':
                return (
                    <div className="d-flex gap-2">
                        {renderAssetButton()}
                        <div className="mb-2">Sold</div>
                        <CButton
                            color="info"
                            size="sm"
                            onClick={() => onView && onView()}
                        >
                            View Details
                        </CButton>
                    </div>
                );
            default:
                return (
                    <div className="d-flex gap-2">
                        {renderAssetButton()}
                        <CButton
                            color="info"
                            size="sm"
                            onClick={() => onView && onView()}
                        >
                            View Details
                        </CButton>
                    </div>
                );
        }
    };

    // Marketplace view - simple card with view button
    if (isMarketplace) {
        return (
            <CCard className="artwork-card marketplace">
                <CCardImage orientation="top" src={imageUrl || defaultArtwork} />
                <CCardBody className="d-flex flex-column">
                    <CCardTitle className="h6 text-truncate">{title}</CCardTitle>
                    <div className="price-tag mb-2">
                        {price} MiEROS
                    </div>
                    <CButton 
                        color="primary"
                        size="sm"
                        className="mt-auto"
                        onClick={(e) => {
                            e.stopPropagation();
                            onView && onView();
                        }}
                    >
                        View
                    </CButton>
                </CCardBody>
            </CCard>
        );
    }

    // MyStuff view - full card with all options
    return (
        <>
            <CCard className="artwork-card mystuff">
                <CCardImage orientation="top" src={imageUrl || defaultArtwork} />
                <CCardBody>
                    <CCardTitle>{title}</CCardTitle>
                    <div className="artwork-details mb-3">
                        <small className="mb-2">
                            Type: {itemType} | Subject: {itemSubject} | Media: {itemMedia}
                        </small>
                    </div>
                    <CCardFooter>
                        {renderActionButton()}
                    </CCardFooter>
                </CCardBody>
            </CCard>

            <ListingHistoryModal
                show={showHistoryModal}
                onHide={() => setShowHistoryModal(false)}
                listingId={listingId}
                nftDetails={{
                    title,
                    description,
                    imageUrl,
                    itemType,
                    itemSubject,
                    itemMedia,
                    price,
                    nftId
                }}
            />

            {itemMedia === 'physical' && (
                <div className="mt-3">
                    <StripeBuyButton 
                        nft={{ id: nftId, title: title, price: price }} 
                        authUser={authUser}
                    />
                </div>
            )}
        </>
    );
};

ArtworkCard.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    itemType: PropTypes.string,
    itemSubject: PropTypes.string,
    itemMedia: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isListed: PropTypes.bool,
    onList: PropTypes.func,
    onCancelListing: PropTypes.func,
    onUpdatePrice: PropTypes.func,
    onView: PropTypes.func,
    listingId: PropTypes.string,
    nftId: PropTypes.string,
    isMarketplace: PropTypes.bool,
    handleClick: PropTypes.func,
    onListForSale: PropTypes.func,
    showListForSale: PropTypes.bool,
    status: PropTypes.string,
    itemAssetUrl: PropTypes.string,
    itemAssetName: PropTypes.string,
    itemAssetType: PropTypes.string,
    assetType: PropTypes.string,
    userId: PropTypes.string,
    currentUserId: PropTypes.string,
    authUser: PropTypes.object
};

export default ArtworkCard;


